
import { useEffect, useRef, useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ReadyToStartSection from '../../../components/feature/ReadyToStartSection';
import Button from '../../../components/base/Button';

export default function Forms() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  const forms = [
    {
      title: 'Enrollment Application',
      description: 'Required application form for new student enrollment.',
      icon: 'ri-file-text-line',
      fileType: 'PDF',
      fileSize: '850 KB',
      color: '#BFE6CC'
    },
    {
      title: 'Medical Forms',
      description: 'Health records, immunization tracking, and medical authorization forms.',
      icon: 'ri-health-book-line',
      fileType: 'PDF',
      fileSize: '1.2 MB',
      color: '#A8D8F0'
    }
  ];

  useEffect(() => {
    const observers = cardsRef.current.map((card, index) => {
      if (!card) return null;
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleCards(prev => [...prev, index]);
            }, index * 100);
          }
        },
        { threshold: 0.2 }
      );
      
      observer.observe(card);
      return observer;
    });

    return () => {
      observers.forEach(observer => observer?.disconnect());
    };
  }, []);

  const handleDownload = (formTitle: string) => {
    // Simulate download - in real implementation, this would link to actual files
    alert(`Downloading ${formTitle}...`);
  };

  return (
    <div className="min-h-screen bg-[#FFF7E8]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#333333] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Forms &amp; Downloads
          </h1>
          <p className="text-xl text-[#333333]/80 max-w-3xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Access all the important documents, forms, and information you need for enrollment.
          </p>
        </div>
      </section>

      {/* Forms Grid */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Enrollment Application */}
            <a 
              href="https://drive.google.com/file/d/1q9M2rU75ZEnDr9e711-Eq2FrV9G9C3Ff/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl shadow-lg p-6 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 cursor-pointer group block"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 bg-[#2E5939]/10 flex items-center justify-center rounded-xl transition-transform duration-150 group-hover:scale-110">
                  <i className="ri-file-text-line text-2xl text-[#2E5939]"></i>
                </div>
                <div className="text-right">
                  <div className="text-xs font-medium text-[#333333]/60 mb-1">PDF</div>
                  <div className="text-xs text-[#333333]/50">245 KB</div>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-[#333333] mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Enrollment Application
              </h3>
              <p className="text-[#333333]/70 text-sm mb-4 leading-relaxed" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                Complete application form for new student enrollment.
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-[#2E5939] group-hover:text-[#BFE6CC] transition-colors duration-200">
                  <i className="ri-download-line mr-2 transition-transform duration-150 group-hover:rotate-12"></i>
                  <span className="text-sm font-medium">Download</span>
                </div>
                <i className="ri-arrow-right-line text-[#333333]/40 group-hover:text-[#2E5939] group-hover:translate-x-1 transition-all duration-200"></i>
              </div>
            </a>

            {/* Medical Forms */}
            <a 
              href="https://drive.google.com/file/d/13NxVpjYezFnCV2URICed2aS8lQU-uJb4/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl shadow-lg p-6 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 cursor-pointer group block"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 bg-[#BFE6CC]/20 flex items-center justify-center rounded-xl transition-transform duration-150 group-hover:scale-110">
                  <i className="ri-heart-pulse-line text-2xl text-[#BFE6CC]"></i>
                </div>
                <div className="text-right">
                  <div className="text-xs font-medium text-[#333333]/60 mb-1">PDF</div>
                  <div className="text-xs text-[#333333]/50">1.2 MB</div>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-[#333333] mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Medical Forms
              </h3>
              <p className="text-[#333333]/70 text-sm mb-4 leading-relaxed" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                Health records, immunization tracking, and medical authorization forms.
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-[#BFE6CC] group-hover:text-[#2E5939] transition-colors duration-200">
                  <i className="ri-download-line mr-2 transition-transform duration-150 group-hover:rotate-12"></i>
                  <span className="text-sm font-medium">Download</span>
                </div>
                <i className="ri-arrow-right-line text-[#333333]/40 group-hover:text-[#BFE6CC] group-hover:translate-x-1 transition-all duration-200"></i>
              </div>
            </a>

            {/* Other Documents */}
            <a 
              href="https://drive.google.com/file/d/1YKyynT4pWIAxaVtMsMohTwwE5yXibDcH/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl shadow-lg p-6 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 cursor-pointer group block"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 bg-[#A8D8F0]/20 flex items-center justify-center rounded-xl transition-transform duration-150 group-hover:scale-110">
                  <i className="ri-folder-line text-2xl text-[#A8D8F0]"></i>
                </div>
                <div className="text-right">
                  <div className="text-xs font-medium text-[#333333]/60 mb-1">PDF</div>
                  <div className="text-xs text-[#333333]/50">950 KB</div>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-[#333333] mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Other Documents
              </h3>
              <p className="text-[#333333]/70 text-sm mb-4 leading-relaxed" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                Additional forms, handbooks, and important documentation for enrollment.
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-[#A8D8F0] group-hover:text-[#2E5939] transition-colors duration-200">
                  <i className="ri-download-line mr-2 transition-transform duration-150 group-hover:rotate-12"></i>
                  <span className="text-sm font-medium">Download</span>
                </div>
                <i className="ri-arrow-right-line text-[#333333]/40 group-hover:text-[#A8D8F0] group-hover:translate-x-1 transition-all duration-200"></i>
              </div>
            </a>

          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Important Information
            </h2>
            <p className="text-lg text-[#333333]/80 max-w-2xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Please review these important notes about our forms and documentation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#A8D8F0]/10 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <i className="ri-information-line text-2xl text-[#A8D8F0] mr-3"></i>
                <h3 className="text-xl font-semibold text-[#333333]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Required Forms
                </h3>
              </div>
              <ul className="space-y-2 text-[#333333]/80" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#BFE6CC] mr-2 mt-1 flex-shrink-0"></i>
                  All enrollment forms must be completed before your child's first day
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#BFE6CC] mr-2 mt-1 flex-shrink-0"></i>
                  Medical forms require physician signature and current immunizations
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#BFE6CC] mr-2 mt-1 flex-shrink-0"></i>
                  Emergency contacts must be available during program hours
                </li>
              </ul>
            </div>
            
            <div className="bg-[#BFE6CC]/10 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <i className="ri-time-line text-2xl text-[#BFE6CC] mr-3"></i>
                <h3 className="text-xl font-semibold text-[#333333]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Submission Deadlines
                </h3>
              </div>
              <ul className="space-y-2 text-[#333333]/80" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#A8D8F0] mr-2 mt-1 flex-shrink-0"></i>
                  Submit enrollment forms at least 2 weeks before start date
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#A8D8F0] mr-2 mt-1 flex-shrink-0"></i>
                  Medical forms must be updated annually
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#A8D8F0] mr-2 mt-1 flex-shrink-0"></i>
                  Supply lists are updated at the beginning of each school year
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <ReadyToStartSection />
      <Footer />
    </div>
  );
}
